import { useEffect } from 'react';

/**
 * Salvaguarda central contra el "page lock" de Radix (cinturón y tirantes — ver DEC del fix de
 * row-actions). Radix DropdownMenu/Dialog en modo `modal` (su default) escribe
 * `pointer-events: none` en `document.body` mientras está abierto y lo restaura en su cleanup al
 * cerrarse. Si el nodo que monta ese menú/diálogo se DESMONTA antes de que corra el cleanup —caso
 * típico: confirmar una acción destructiva en una fila → la mutación borra el registro → la query se
 * invalida → la fila (con su menú/diálogo) se desmonta— el body queda con `pointer-events: none` y
 * toda la página deja de responder a clicks hasta hacer refresh.
 *
 * El fix primario es `modal={false}` en los DropdownMenu de los *RowActions* locales. Pero el
 * `ActionMenu` de `@fiado/react-kit` no expone esa prop (cambiarlo es un cambio de kit, pendiente de
 * aprobación), así que este guard es el respaldo que cubre ese caso sin tocar el kit.
 *
 * Mecanismo: observa cambios de estilo en `<body>`; cuando detecta `pointer-events: none` pero NO hay
 * ningún portal/overlay de Radix realmente montado en el DOM (es decir, ningún menú/diálogo abierto
 * que justifique el lock), lo limpia. Mientras haya un overlay legítimo abierto NO interfiere, así que
 * no rompe el comportamiento modal de los diálogos que sí lo quieren.
 *
 * Se monta una sola vez, a nivel raíz de la app.
 */
export function BodyPointerEventsGuard() {
    useEffect(() => {
        const body = document.body;

        const radixOverlayOpen = (): boolean =>
            // Cualquier content de menú/diálogo/popover actualmente montado y abierto.
            // OJO: `[role="dialog"]` SIN `[data-state="open"]` — el `Modal` de @fiado/react-kit
            // expone role="dialog" pero NO el data-state de Radix. Exigir data-state hacía que el
            // guard NO reconociera ese modal abierto y limpiara el pointer-events del body → el modal
            // (ej. "ver detalle de tenant") quedaba inclicable. Con role="dialog" a secas lo cubre.
            document.querySelector(
                '[data-radix-popper-content-wrapper], [role="dialog"], [data-radix-menu-content][data-state="open"]',
            ) !== null;

        const fixIfStuck = (): void => {
            if (body.style.pointerEvents === 'none' && !radixOverlayOpen()) {
                body.style.pointerEvents = '';
            }
        };

        // Diferimos el chequeo un frame: cuando un overlay LEGÍTIMO se está abriendo, Radix puede
        // setear el `pointer-events: none` del body en el mismo tick en que monta el content. Esperar
        // al siguiente frame evita limpiar un lock recién creado y válido; solo actuamos sobre locks
        // que persisten sin overlay (los huérfanos que dejan colgada la página).
        let raf = 0;
        const schedule = (): void => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(fixIfStuck);
        };

        const observer = new MutationObserver(schedule);
        observer.observe(body, { attributes: true, attributeFilter: ['style'] });

        // Chequeo inicial por si quedó trabado de un render previo.
        schedule();

        return () => {
            cancelAnimationFrame(raf);
            observer.disconnect();
            // Al desmontar la app garantizamos que el body queda clickable.
            body.style.pointerEvents = '';
        };
    }, []);

    return null;
}
